/**
 * Custom toast system (replaces ant-design message for our app).
 * Renders a vertically-stacked column at top-right. Each toast has:
 *   - one of 5 type colors (success/info/warn/error/deal)
 *   - a 2px progress bar that animates down
 *   - click to dismiss with slide-out
 *   - auto-dismiss after a duration (deal = 4500ms, normal = 3500ms)
 *
 * Usage: import { toast } from '@/lib/toast'; toast.deal('成交！'); toast.success('保存成功');
 */
import { createRoot, Root } from 'react-dom/client';
import { useEffect, useState } from 'react';

type ToastType = 'success' | 'info' | 'warn' | 'error' | 'deal';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  duration: number;
  /** IM5：可点击跳转(如新消息 toast 点击跳会话)；点击后先执行再 dismiss。 */
  onClick?: () => void;
}

const ICONS: Record<ToastType, JSX.Element> = {
  success: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 13l4 4L19 7" /></svg>,
  info:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>,
  warn:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>,
  error:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>,
  deal:    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L13.5 8L20 9L15 13.5L16.5 20L12 16.5L7.5 20L9 13.5L4 9L10.5 8Z" /></svg>,
};

class ToastBus extends EventTarget {
  emit(item: Omit<ToastItem, 'id'>) {
    this.dispatchEvent(new CustomEvent('toast', { detail: { ...item, id: Date.now() + Math.random() } }));
  }
}
const bus = new ToastBus();

let mounted = false;
let root: Root | null = null;

function ensureMounted() {
  if (mounted) return;
  mounted = true;
  const div = document.createElement('div');
  document.body.appendChild(div);
  root = createRoot(div);
  root.render(<ToastHost />);
}

function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    const onToast = (e: Event) => {
      const item = (e as CustomEvent).detail as ToastItem;
      setItems((arr) => [...arr, item]);
    };
    bus.addEventListener('toast', onToast);
    return () => bus.removeEventListener('toast', onToast);
  }, []);

  const remove = (id: number) => {
    setItems((arr) => arr.map((t) => (t.id === id ? { ...t, leaving: true } : t)) as any);
    setTimeout(() => setItems((arr) => arr.filter((t) => t.id !== id)), 250);
  };

  return (
    <div className="huanke-toast-container">
      {items.map((t: any) => (
        <ToastCard key={t.id} item={t} leaving={t.leaving} onDismiss={() => remove(t.id)} />
      ))}
    </div>
  );
}

function ToastCard({ item, leaving, onDismiss }: { item: ToastItem; leaving?: boolean; onDismiss: () => void }) {
  useEffect(() => {
    const id = setTimeout(onDismiss, item.duration);
    return () => clearTimeout(id);
  }, [item.duration, onDismiss]);

  return (
    <div
      className={`huanke-toast toast-${item.type} ${leaving ? 'leaving' : ''}`}
      onClick={() => { item.onClick?.(); onDismiss(); }}
      role="status"
    >
      {ICONS[item.type]}
      <span>{item.message}</span>
      <div className="toast-progress" style={{ animationDuration: `${item.duration}ms` }} />
    </div>
  );
}

/**
 * 去重：同 (type,message) 200ms 窗口内只展示一次。
 *
 * 业务侧多源（axios interceptor + mutation.onError + WS 广播）同条错误
 * 同时触发 toast 时只显示一个；间隔 > 200ms 不算重复（如用户主动连续操作）。
 *
 * 不去重过度：不同 message / 不同 type 各自计数；250ms 是肉眼仍然能感知
 * "几乎同时"的窗口，更长会误吞用户真重复的操作。
 */
const recentToasts = new Map<string, number>();
const DEDUP_WINDOW_MS = 250;

function emit(type: ToastType, message: string, duration?: number, onClick?: () => void) {
  const key = `${type}:${message}`;
  const now = Date.now();
  const last = recentToasts.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) {
    // 重复 — 静默吞掉
    return;
  }
  recentToasts.set(key, now);
  // 清理 > 60s 的旧记录，防止内存累积
  if (recentToasts.size > 100) {
    for (const [k, t] of recentToasts.entries()) {
      if (now - t > 60_000) recentToasts.delete(k);
    }
  }
  ensureMounted();
  bus.emit({ type, message, duration: duration ?? (type === 'deal' ? 4500 : 3500), onClick });
}

export const toast = {
  success: (m: string, d?: number) => emit('success', m, d),
  info:    (m: string, d?: number) => emit('info', m, d),
  warn:    (m: string, d?: number) => emit('warn', m, d),
  error:   (m: string, d?: number) => emit('error', m, d),
  /** Use for L7 成交 — gold gradient, 4.5s, paired with confetti */
  deal:    (m: string, d?: number) => emit('deal', m, d),
  /** IM5 微信式新消息提示：可点击跳会话。onClick 由调用方(Layout)传入 nav 闭包(在 Router 内，SPA 不刷新)。 */
  imMessage: (m: string, onClick: () => void, d?: number) => emit('info', m, d ?? 5000, onClick),
};
